import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';

async function getAuthToken() {
  const { tokens } = await fetchAuthSession();
  const idToken = tokens?.idToken;
  if (!idToken) return null;
  const currentTime = Math.floor(Date.now() / 1000);
  if (idToken.payload?.exp != null && idToken.payload.exp <= currentTime) return null;
  return idToken.toString();
}

async function requestJson(endpoint, { method = 'GET', body = null, signal } = {}) {
  const token = await getAuthToken();
  if (!token) {
    const err = new Error('Authentication token missing/expired.');
    err.status = 401;
    throw err;
  }

  const url = `${API_BASE_URL}${endpoint}`;
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  let res;
  try {
    res = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (e) {
    loggingService
      .logError('api_error_camera_attendance', `Network error calling ${endpoint}`, e, {
        endpoint,
        method,
      })
      .catch(() => {});
    throw e;
  }

  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const payload = isJson ? await res.json().catch(() => null) : await res.text().catch(() => '');

  if (!res.ok) {
    const err = new Error(
      (payload && typeof payload === 'object' && (payload.message || payload.error)) ||
        `HTTP ${res.status} ${res.statusText}`
    );
    err.status = res.status;
    err.statusText = res.statusText;
    err.endpoint = endpoint;
    err.responseData = payload;

    loggingService
      .logError('api_error_camera_attendance', `API error calling ${endpoint}`, err, {
        endpoint,
        method,
        status: res.status,
        statusText: res.statusText,
        responseData: payload,
      })
      .catch(() => {});
    throw err;
  }

  return payload;
}

export const cameraAttendanceApi = {
  getAttendanceOverstay: async ({ site_id, date, signal } = {}) => {
    const qs = new URLSearchParams();
    if (site_id) qs.set('site_id', String(site_id));
    if (date) qs.set('date', String(date));
    const data = await requestJson(`/attendance/overstay?${qs.toString()}`, { signal });
    return Array.isArray(data) ? data : data?.items || data?.data || [];
  },

  forceCheckoutAttendance: async ({ a_id, reason, signal } = {}) => {
    if (!a_id) throw new Error('a_id is required');
    const body = reason ? { reason } : {};
    return await requestJson(`/attendance/${encodeURIComponent(String(a_id))}/force-checkout`, {
      method: 'PUT',
      body,
      signal,
    });
  },

  getVisitorOverstay: async ({ site_id, date, signal } = {}) => {
    const qs = new URLSearchParams();
    if (site_id) qs.set('site_id', String(site_id));
    if (date) qs.set('date', String(date));
    const data = await requestJson(`/visitor/overstay?${qs.toString()}`, { signal });
    return Array.isArray(data) ? data : data?.items || data?.data || [];
  },

  forceCheckoutVisitor: async ({ entry_id, reason, signal } = {}) => {
    if (!entry_id) throw new Error('entry_id is required');
    const body = reason ? { reason } : {};
    return await requestJson(`/visitor-entries/${encodeURIComponent(String(entry_id))}/force-checkout`, {
      method: 'PUT',
      body,
      signal,
    });
  },

  getDailyWageOverstay: async ({ site_id, date, signal } = {}) => {
    const qs = new URLSearchParams();
    if (site_id) qs.set('site_id', String(site_id));
    if (date) qs.set('date', String(date));
    const data = await requestJson(`/daily-wage/overstay?${qs.toString()}`, { signal });
    return Array.isArray(data) ? data : data?.items || data?.data || [];
  },

  forceCheckoutDailyWage: async ({ entry_id, reason, signal } = {}) => {
    if (!entry_id) throw new Error('entry_id is required');
    const body = reason ? { reason } : {};
    return await requestJson(`/daily-wage/${encodeURIComponent(String(entry_id))}/force-checkout`, {
      method: 'PUT',
      body,
      signal,
    });
  },

  resolveSafetyViolation: async ({ sv_id, signal } = {}) => {
    if (!sv_id) throw new Error('sv_id is required');
    return await requestJson(`/safety/violations/${encodeURIComponent(String(sv_id))}/resolve`, {
      method: 'PUT',
      body: {},
      signal,
    });
  },
};

